import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiResult } from '@web/api';

const baseContext = {
  queueRequest: request => {},
  queueRequests: requests => {},
};

const defaultApiRetryAllowance = 2;

export const RequestQueueContext = createContext(baseContext);

export const useRequestQueue = () => useContext(RequestQueueContext);

export const RequestQueueProvider = ({ children }) => {
  const requestQueue = useRef([]);
  const processingQueue = useRef(false);
  const apiRetryAllowance = useRef(defaultApiRetryAllowance);
  const [requestQueueLength, setRequestQueueLength] = useState(
    requestQueue.current.length,
  );

  const queueRequest = useCallback(request => {
    requestQueue.current.push(request);
    setRequestQueueLength(requestQueue.current.length);
  }, []);

  const queueRequests = useCallback(requests => {
    requestQueue.current.push(...requests);
    setRequestQueueLength(requestQueue.current.length);
  }, []);

  const processRequest = useCallback(async (request, retryWaitTime = 500) => {
    const result = await request();
    ApiResult.match(result, {
      error: {
        server: async () => {
          if (apiRetryAllowance.current > 0) {
            apiRetryAllowance.current -= 1;
            await new Promise(r => setTimeout(r, retryWaitTime));
            await processRequest(request, retryWaitTime + 300);
          }
        },
      },
    });
  }, []);

  useEffect(() => {
    const processQueue = async () => {
      if (processingQueue.current) return;
      processingQueue.current = true;
      while (requestQueue.current.length > 0) {
        const request = requestQueue.current.shift();
        await processRequest(request);
        apiRetryAllowance.current = defaultApiRetryAllowance;
        setRequestQueueLength(requestQueue.current.length);
      }
      processingQueue.current = false;
    };
    if (requestQueueLength > 0) {
      processQueue();
    }
  }, [requestQueueLength, processRequest]);

  return (
    <RequestQueueContext.Provider
      value={useMemo(
        () => ({ queueRequest, queueRequests }),
        [queueRequest, queueRequests],
      )}
    >
      {children}
    </RequestQueueContext.Provider>
  );
};
